import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { ActionIcon, Checkbox, Tooltip } from "@mantine/core"
import { IconRotateClockwise } from "@tabler/icons-react"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

/**
 * JargonHistoryRow renders one already-decided suggestion in the Accepted /
 * Dismissed history views as a compact single-line row: a selection checkbox, the
 * term (read-only — history is a record, not an editor), its description, and a
 * "Restore to pending" action. The decision status is uniform across a history
 * view (the segmented control already names it), so no per-row badge repeats it.
 * Dumb component: selection + restore are reported to the host. Restoring an
 * accepted term only re-queues it for review; it never touches the dictionary.
 */
export function JargonHistoryRow({
  suggestion,
  selected,
  onToggleSelect,
  onRestore,
  busy = false,
  exiting = false,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()

  return (
    <div
      className={classes.row}
      data-busy={busy || undefined}
      data-selected={selected || undefined}
      data-exiting={exiting || undefined}
      aria-disabled={busy || undefined}
    >
      <Checkbox
        checked={selected}
        onChange={() => onToggleSelect(suggestion.id)}
        aria-label={i18n._(msg`Select ${suggestion.term}`)}
        className={classes.checkbox}
      />
      <span className={classes.term}>{suggestion.term}</span>
      {suggestion.frequency > 1 && <span className={classes.freq}>×{suggestion.frequency}</span>}
      {suggestion.context && (
        <span className={classes.context} title={suggestion.context}>
          {suggestion.context}
        </span>
      )}
      <Tooltip label={i18n._(msg`Restore to pending`)} withArrow>
        <ActionIcon
          variant="subtle"
          color="brand"
          size="md"
          className={classes.restore}
          onClick={() => onRestore(suggestion.id)}
          loading={busy}
          aria-label={i18n._(msg`Restore ${suggestion.term} to pending`)}
        >
          <IconRotateClockwise size={16} />
        </ActionIcon>
      </Tooltip>
    </div>
  )
}
